import React from "react";
import { create, toCssValue } from "jss";
import { StylesProvider, jssPreset } from "@material-ui/core/styles";
import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/core";

// Material-UI (JSS) and react-select (emotion) generate CSS at runtime and by default
// write it into <style> tags, which a Content-Security-Policy of `style-src 'self'`
// blocks. CSP does not apply to the CSSOM, so this module writes the same CSS into
// constructable stylesheets adopted by the document instead. No 'unsafe-inline' needed.

export const isAdoptedStyleSheetsSupported =
  typeof document !== "undefined" &&
  typeof CSSStyleSheet !== "undefined" &&
  "adoptedStyleSheets" in Document.prototype &&
  "replaceSync" in CSSStyleSheet.prototype;

// --- document.adoptedStyleSheets bookkeeping -------------------------------------------

const ownedSheets = new WeakSet();
const attachedSheets = [];
let attachCounter = 0;

const syncAdoptedSheets = () => {
  const foreign = document.adoptedStyleSheets.filter((s) => !ownedSheets.has(s));
  const ordered = [...attachedSheets]
    .sort((a, b) => a.index - b.index || a.order - b.order)
    .map((entry) => entry.css);
  document.adoptedStyleSheets = [...foreign, ...ordered];
};

const createOwnedSheet = (media) => {
  const css = media ? new CSSStyleSheet({ media }) : new CSSStyleSheet();
  ownedSheets.add(css);
  return css;
};

// Same ordering JSS uses for <style> tags: lower sheet index first, then attach order.
const adoptSheet = (css, index) => {
  if (attachedSheets.some((entry) => entry.css === css)) return;
  attachedSheets.push({ css, index, order: attachCounter++ });
  syncAdoptedSheets();
};

const dropSheet = (css) => {
  const position = attachedSheets.findIndex((entry) => entry.css === css);
  if (position === -1) return;
  attachedSheets.splice(position, 1);
  syncAdoptedSheets();
};

// --- JSS renderer (Material-UI, material-table, pickers) --------------------------------
// Mirrors JSS's built-in DomRenderer, with a constructable CSSStyleSheet in place of the
// <style> element.

const insertNativeRule = (container, ruleText, index) => {
  try {
    if ("insertRule" in container) container.insertRule(ruleText, index);
    else if ("appendRule" in container) container.appendRule(ruleText);
  } catch (err) {
    // Browser rejected the rule (e.g. another engine's vendor prefix) — skip it, as JSS does.
    return false;
  }
  return container.cssRules[index];
};

const getValidRuleInsertionIndex = (container, index) => {
  const maxIndex = container.cssRules.length;
  if (index === undefined || index > maxIndex) return maxIndex;
  return index;
};

class AdoptedSheetRenderer {
  constructor(sheet) {
    this.sheet = sheet;
    this.cssRules = [];
    this.css = sheet ? createOwnedSheet(sheet.options.media) : null;
  }

  getPropertyValue(cssRule, prop) {
    try {
      return cssRule.style.getPropertyValue(prop);
    } catch (err) {
      return "";
    }
  }

  setProperty(cssRule, prop, value) {
    try {
      const cssValue = Array.isArray(value) ? toCssValue(value) : value;
      const importantIndex = cssValue ? String(cssValue).indexOf("!important") : -1;
      const valueWithoutImportant =
        importantIndex > -1 ? String(cssValue).substr(0, importantIndex - 1) : cssValue;
      cssRule.style.setProperty(prop, valueWithoutImportant, importantIndex > -1 ? "important" : "");
    } catch (err) {
      return false;
    }
    return true;
  }

  removeProperty(cssRule, prop) {
    try {
      cssRule.style.removeProperty(prop);
    } catch (err) {
      // Ignore, as JSS does.
    }
  }

  setSelector(cssRule, selectorText) {
    cssRule.selectorText = selectorText;
    return cssRule.selectorText === selectorText;
  }

  attach() {
    if (!this.sheet) return;
    adoptSheet(this.css, this.sheet.options.index || 0);
  }

  detach() {
    if (!this.sheet) return;
    dropSheet(this.css);
  }

  deploy() {
    const { sheet } = this;
    if (!sheet) return;
    if (sheet.options.link) {
      this.insertRules(sheet.rules);
      return;
    }
    this.css.replaceSync(sheet.toString());
  }

  insertRules(rules, nativeParent) {
    for (let i = 0; i < rules.index.length; i++) {
      this.insertRule(rules.index[i], i, nativeParent);
    }
  }

  insertRule(rule, index, nativeParent = this.css) {
    if (rule.rules) {
      let latestNativeParent = nativeParent;
      if (rule.type === "conditional" || rule.type === "keyframes") {
        const insertionIndex = getValidRuleInsertionIndex(nativeParent, index);
        // Render the container rule without children first, then fill it.
        latestNativeParent = insertNativeRule(
          nativeParent,
          rule.toString({ children: false }),
          insertionIndex
        );
        if (latestNativeParent === false) return false;
        this.refCssRule(rule, insertionIndex, latestNativeParent);
      }
      this.insertRules(rule.rules, latestNativeParent);
      return latestNativeParent;
    }

    const ruleText = rule.toString();
    if (!ruleText) return false;
    const insertionIndex = getValidRuleInsertionIndex(nativeParent, index);
    const nativeRule = insertNativeRule(nativeParent, ruleText, insertionIndex);
    if (nativeRule === false) return false;
    this.refCssRule(rule, insertionIndex, nativeRule);
    return nativeRule;
  }

  refCssRule(rule, index, cssRule) {
    rule.renderable = cssRule;
    // Only top-level rules are tracked; deleteRule can't remove nested ones.
    if (rule.options.parent === this.sheet) {
      this.cssRules.splice(index, 0, cssRule);
    }
  }

  deleteRule(cssRule) {
    const index = this.indexOf(cssRule);
    if (index === -1) return false;
    this.css.deleteRule(index);
    this.cssRules.splice(index, 1);
    return true;
  }

  indexOf(cssRule) {
    return this.cssRules.indexOf(cssRule);
  }

  replaceRule(cssRule, rule) {
    const index = this.indexOf(cssRule);
    if (index === -1) return false;
    this.css.deleteRule(index);
    this.cssRules.splice(index, 1);
    return this.insertRule(rule, index);
  }

  getRules() {
    return this.css.cssRules;
  }
}

// --- emotion sheet (react-select) -------------------------------------------------------

const createAdoptedEmotionSheet = (key) => {
  const css = createOwnedSheet();
  return {
    key,
    isSpeedy: true,
    container: document.head,
    tags: [],
    ctr: 0,
    insert(ruleText) {
      adoptSheet(css, Number.MAX_SAFE_INTEGER);
      try {
        css.insertRule(ruleText, css.cssRules.length);
      } catch (err) {
        // Browser rejected the rule (e.g. another engine's vendor prefix) — skip it,
        // as emotion's own speedy mode does.
      }
    },
    flush() {
      css.replaceSync("");
    },
  };
};

// --- provider --------------------------------------------------------------------------

let jss;
let emotionCache;

if (isAdoptedStyleSheetsSupported) {
  jss = create({ ...jssPreset(), Renderer: AdoptedSheetRenderer });
  emotionCache = createCache({ key: "css" });
  emotionCache.sheet = createAdoptedEmotionSheet("css");
}

export default function CspSafeStylesProvider({ children }) {
  // Older browsers (and jsdom in tests) keep the libraries' default <style> tags.
  if (!isAdoptedStyleSheetsSupported) return children;
  return (
    <StylesProvider jss={jss}>
      <CacheProvider value={emotionCache}>{children}</CacheProvider>
    </StylesProvider>
  );
}
